<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { INPUT_TYPES } from 'dashboard/components-next/taginput/helper/tagInputHelper.js';
import {
  isPhoneLike,
  normalizePhoneInput,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contacts: {
    type: Array,
    required: true,
  },
  selectedContact: {
    type: Object,
    default: null,
  },
  showContactsDropdown: {
    type: Boolean,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  isCreatingContact: {
    type: Boolean,
    required: true,
  },
  contactId: {
    type: String,
    default: null,
  },
  contactableInboxesList: {
    type: Array,
    default: () => [],
  },
  showInboxesDropdown: {
    type: Boolean,
    required: true,
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'searchContacts',
  'setSelectedContact',
  'clearSelectedContact',
  'updateDropdown',
]);

const i18nPrefix = 'COMPOSE_NEW_CONVERSATION.FORM.CONTACT_SELECTOR';
const { t } = useI18n();

const inputType = ref(INPUT_TYPES.EMAIL);
const pendingPhone = ref(null);
const customName = ref('');
const customNameRef = ref(null);

const contactsList = computed(() => {
  return props.contacts?.map(({ name, id, thumbnail, email, ...rest }) => ({
    id,
    label: email ? `${name} (${email})` : name,
    value: id,
    thumbnail: { name, src: thumbnail },
    ...rest,
    name,
    email,
    action: 'contact',
  }));
});

const selectedContactLabel = computed(() => {
  const { name, email = '', phoneNumber = '' } = props.selectedContact || {};
  if (email) {
    return `${name} (${email})`;
  }
  if (phoneNumber) {
    return `${name} (${phoneNumber})`;
  }
  return name || '';
});

const errorClass = computed(() => {
  return props.hasErrors
    ? '[&_input]:placeholder:!text-n-ruby-9 [&_input]:dark:placeholder:!text-n-ruby-9'
    : '';
});

const handleInput = value => {
  // TEL when phone-like (with or without '+'), else EMAIL. Drives validation
  // in the TagInput so the "create" chip shows up for raw digit input too.
  inputType.value = isPhoneLike(value) ? INPUT_TYPES.TEL : INPUT_TYPES.EMAIL;
  emit('searchContacts', value);
};

const handleAdd = async item => {
  // Intercept 'create' for phone-like values — collect a name inline first
  // instead of using the number itself as the contact name.
  if (item?.action === 'create' && isPhoneLike(item.value)) {
    pendingPhone.value = normalizePhoneInput(item.value);
    customName.value = '';
    await nextTick();
    customNameRef.value?.focus();
    return;
  }
  emit('setSelectedContact', item);
};

const confirmPhoneCreate = () => {
  const name = customName.value.trim();
  if (!name || !pendingPhone.value) return;
  emit('setSelectedContact', {
    value: pendingPhone.value,
    action: 'create',
    name,
  });
  pendingPhone.value = null;
  customName.value = '';
};

const cancelPhoneCreate = () => {
  pendingPhone.value = null;
  customName.value = '';
};
</script>

<template>
  <div class="relative flex-1 px-4 py-3 overflow-y-visible">
    <div class="flex items-baseline w-full gap-3 min-h-7">
      <label class="text-sm font-medium text-n-slate-11 whitespace-nowrap">
        {{ t(`${i18nPrefix}.LABEL`) }}
      </label>

      <div
        v-if="isCreatingContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 px-3 min-h-7 min-w-0"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{ t(`${i18nPrefix}.CONTACT_CREATING`) }}
        </span>
      </div>
      <div
        v-else-if="selectedContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 min-h-7 min-w-0"
        :class="!contactId ? 'ltr:pl-3 rtl:pr-3 ltr:pr-1 rtl:pl-1' : 'px-3'"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{
            isCreatingContact
              ? t(`${i18nPrefix}.CONTACT_CREATING`)
              : selectedContactLabel
          }}
        </span>
        <Button
          v-if="!contactId"
          variant="ghost"
          icon="i-lucide-x"
          color="slate"
          :disabled="contactId"
          size="xs"
          @click="emit('clearSelectedContact')"
        />
      </div>
      <div
        v-else-if="pendingPhone"
        class="flex items-center flex-1 gap-2 rounded-md bg-n-alpha-2 px-3 min-h-7 min-w-0"
      >
        <span class="text-xs whitespace-nowrap text-n-slate-11">
          {{ pendingPhone }}
        </span>
        <input
          ref="customNameRef"
          v-model="customName"
          type="text"
          :placeholder="t(`${i18nPrefix}.NAME_PLACEHOLDER`)"
          class="flex-1 min-w-0 !h-6 !py-0 !px-2 !text-sm !bg-transparent !border-0 !outline-none !rounded focus:!ring-1 focus:!ring-n-brand"
          @keyup.enter="confirmPhoneCreate"
          @keyup.esc="cancelPhoneCreate"
        />
        <Button
          size="xs"
          :label="t(`${i18nPrefix}.CREATE`)"
          :disabled="!customName.trim()"
          @click="confirmPhoneCreate"
        />
        <Button
          variant="ghost"
          icon="i-lucide-x"
          color="slate"
          size="xs"
          @click="cancelPhoneCreate"
        />
      </div>
      <TagInput
        v-else
        :placeholder="t(`${i18nPrefix}.TAG_INPUT_PLACEHOLDER`)"
        mode="single"
        :menu-items="contactsList"
        :show-dropdown="showContactsDropdown"
        :is-loading="isLoading"
        :disabled="contactableInboxesList?.length > 0 && showInboxesDropdown"
        allow-create
        :type="inputType"
        class="flex-1 min-h-7"
        :class="errorClass"
        focus-on-mount
        @input="handleInput"
        @on-click-outside="emit('updateDropdown', 'contacts', false)"
        @add="handleAdd"
        @remove="emit('clearSelectedContact')"
      />
    </div>
  </div>
</template>
